import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import apiService from '../services/apiService';
import StripeWrapper from '../components/StripeWrapper';
import StripePaymentForm from '../components/StripePaymentForm';
import './ZonePage.css';

const ZonePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [zoneId, setZoneId] = useState('');
  const [licensePlate, setLicensePlate] = useState('');
  const [email, setEmail] = useState('');
  const [duration, setDuration] = useState(60); // minutes (1 hour)
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [error, setError] = useState(null);
  // Existing-session UI is read-only for now; setters intentionally omitted
  // until backend session-lookup is wired up.
  const [hasExistingSession] = useState(false);
  const [existingSessionDetails] = useState(null);
  const [zoneDetails, setZoneDetails] = useState(null);
  const [pricingDetails, setPricingDetails] = useState({
    hourly_rate: 0,
    base_fee: 0,
    calculated_price: null
  });
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentIntent, setPaymentIntent] = useState(null);

  const fetchZoneDetails = async (zoneCode) => {
    try {
      setInitialLoading(true);
      setError(null);
      setZoneDetails(null); 
      setPricingDetails({ hourly_rate: 0, base_fee: 0, calculated_price: null });

      const response = await apiService.getZonePricing(zoneCode, duration);
      if (response.status === 'success' && response.data && response.data.zone) {
        setZoneDetails(response.data.zone);
        setPricingDetails({
          hourly_rate: response.data.hourly_rate,
          base_fee: response.data.base_fee,
          calculated_price: response.data.calculated_price
        });
      } else {
        const message = response.message || (response.data && response.data.message) || 'Invalid zone ID or zone not found.';
        setError(message);
        setZoneDetails(null);
        setPricingDetails({ hourly_rate: 0, base_fee: 0, calculated_price: null });
      }
    } catch (error) {
      console.error('Error fetching zone details:', error);
      setError(error.message || 'Failed to load zone details. Please try again.');
      setZoneDetails(null);
      setPricingDetails({ hourly_rate: 0, base_fee: 0, calculated_price: null });
    } finally {
      setInitialLoading(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const zoneParam = params.get('zone');
    if (zoneParam) {
      setZoneId(zoneParam);
      fetchZoneDetails(zoneParam);
    }
    // Intentionally runs only when the URL query changes; fetchZoneDetails is
    // stable enough for this one-shot read and excluded on purpose.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    const updatePricing = async () => {
      if (zoneDetails && zoneId && duration) { 
        try {
          setLoading(true);
          // setError(null); 
          const response = await apiService.getZonePricing(zoneId, duration);
          if (response.status === 'success' && response.data && response.data.calculated_price) {
            setPricingDetails({
              hourly_rate: response.data.hourly_rate,
              base_fee: response.data.base_fee,
              calculated_price: response.data.calculated_price
            });
            setError(null);
          } else {
            setError(response.message || 'Failed to update pricing for the selected duration.');
            setPricingDetails(prev => ({ ...prev, calculated_price: null }));
          }
        } catch (error) {
          console.error('Error updating pricing:', error);
          setError(error.message || 'Failed to update pricing.');
          setPricingDetails(prev => ({ ...prev, calculated_price: null }));
        } finally {
          setLoading(false);
        }
      }
    };

  

    if (zoneDetails && !initialLoading) {
      updatePricing();
    }
    
  }, [duration, zoneId, zoneDetails, initialLoading]);
  
  const getExpirationTime = () => {
    const now = new Date();
    const expiration = new Date(now.getTime() + duration * 60 * 1000);
    return expiration.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

 const handleZoneIdSearch = async () => {
  if (!zoneId.trim()) {
    setError("Zone ID cannot be empty.");
    setZoneDetails(null);
    setPricingDetails({ hourly_rate: 0, base_fee: 0, calculated_price: null });
    return;
  }
  setLoading(true);
  setError(null);
  setZoneDetails(null); 
  setPricingDetails({ hourly_rate: 0, base_fee: 0, calculated_price: null });

  try {
    const response = await apiService.getZonePricing(zoneId, duration); 
    if (response.status === 'success' && response.data && response.data.zone) {
      setZoneDetails(response.data.zone);
      setPricingDetails({
        hourly_rate: response.data.hourly_rate,
        base_fee: response.data.base_fee,
        calculated_price: response.data.calculated_price,
      });
    } else {
      const message = response.message || (response.data && response.data.message) || 'Invalid zone ID or zone not found.';
      setError(message);
      
    }
  } catch (error) {
    console.error('Error in handleZoneIdSearch:', error);
    setError(error.message || 'Failed to search for zone. Please try again.');
    
  } finally {
    setLoading(false);
  }
 };

  const handleLicensePlateChange = (e) => {
    setLicensePlate(e.target.value.toUpperCase());
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
  };

  const handleZoneIdInputChange = (e) => {
    const newZoneId = e.target.value;
    setZoneId(newZoneId);
    
    if (zoneDetails) {
      setZoneDetails(null);
    }
    if (pricingDetails.calculated_price) {
       setPricingDetails({ hourly_rate: 0, base_fee: 0, calculated_price: null });
    }
    if (error) { 
      setError(null);
    }
  };

  const handleDurationChange = (e) => {
    setDuration(parseInt(e.target.value, 10));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await apiService.createPaymentIntent({
        amount: pricingDetails.calculated_price.total_price,
        email,
        zone_code: zoneId,
        licensePlate,
        duration,
        parkingFee: pricingDetails.calculated_price.parking_fee,
        processingFee: pricingDetails.calculated_price.processing_fee
      });
      
      if (response.status === 'success' && response.data.clientSecret) {
        setPaymentIntent({
          clientSecret: response.data.clientSecret,
          amount: pricingDetails.calculated_price.total_price,
          paymentIntentId: response.data.paymentIntentId
        });
        setShowPaymentForm(true);
      } else {
        throw new Error(response.message || 'Failed to create payment');
      }
    } catch (error) {
      console.error('Error creating payment intent:', error);
      setError(error.message || 'An error occurred while preparing payment');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = async (paymentIntent) => {
    try {
      setLoading(true);
      
      const sessionResponse = await apiService.createParkingSession({
        licensePlate,
        email,
        zoneId,
        duration
      });
      
      if (sessionResponse.status === 'success') {
        navigate('/payment-success', { 
          state: { 
            sessionDetails: sessionResponse.data.session,
            paymentIntentId: paymentIntent.id
          } 
        });
      } else {
        throw new Error(sessionResponse.message || 'Failed to create parking session');
      }
    } catch (error) {
      console.error('Error creating parking session:', error);
      setError(error.message || 'Payment succeeded but session creation failed');
      
      navigate(`/payment-status/${paymentIntent.id}`);
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentError = (errorMessage) => {
    setError(errorMessage);
    setShowPaymentForm(false);
  };

  const handleGoBack = () => {
    setShowPaymentForm(false);
    setPaymentIntent(null);
  };

  const handleCheckStatus = () => {
    if (paymentIntent && paymentIntent.paymentIntentId) {
      navigate(`/payment-status/${paymentIntent.paymentIntentId}`);
    }
  };

  return (
    <div className="zone-container">
      <header className="zone-header">
        <h1>RGVSMARTPAY</h1>
      </header>

      <main className="zone-content">
        <div className="zone-form-container">
          <h2>Park Your Vehicle</h2>
          {!showPaymentForm && (
            <p className="zone-subtitle">Enter your details to start or extend parking</p>
          )}

          {error && <div className="error-message">{error}</div>}

          {initialLoading ? (
            <div className="zone-loading">Loading zone details...</div>
          ) : showPaymentForm ? (
            <>
              <div className="payment-summary">
                <h3>Parking Details</h3>
                <div className="summary-row">
                  <span>Zone:</span>
                  <span>{zoneDetails?.name} ({zoneId})</span>
                </div>
                
                <div className="summary-row">
                  <span>License Plate:</span>
                  <span>{licensePlate}</span>
                </div>
                <div className="summary-row">
                  <span>Duration:</span>
                  <span>{duration / 60} hour{duration / 60 !== 1 ? 's' : ''}</span>
                </div>
                <div className="summary-row">
                  <span>Expires at:</span>
                  <span>{getExpirationTime()}</span>
                </div>
              </div>
              
              <div className="back-button-container">
                <button 
                  onClick={handleGoBack} 
                  className="back-button"
                  disabled={loading}
                >
                  Back to Details
                </button>
                {paymentIntent && paymentIntent.paymentIntentId && (
                  <button
                    onClick={handleCheckStatus}
                    className="back-button check-status-button"
                    disabled={loading}
                  >
                    Check Payment Status
                  </button>
                )}
              </div>
              
              <StripeWrapper>
                <StripePaymentForm 
                  amount={paymentIntent.amount}
                  clientSecret={paymentIntent.clientSecret}
                  metadata={{ email }}
                  onPaymentSuccess={handlePaymentSuccess}
                  onPaymentError={handlePaymentError}
                />
              </StripeWrapper>
            </>
          ) : (
            <>
              {hasExistingSession ? (
                <div className="existing-session">
                  <h3>Extend Existing Session</h3>
                  <p>You already have an active parking session:</p>
                  <div className="session-details">
                    <p><strong>License Plate:</strong> {existingSessionDetails?.licensePlate}</p>
                    <p><strong>Expires:</strong> {existingSessionDetails?.expirationTime}</p>
                    <p><strong>Time Remaining:</strong> {existingSessionDetails?.timeRemaining}</p>
                  </div>
                </div>
              ) : null}

              {zoneDetails && !error && ( 
                <div className="zone-details">
                  <h3>Zone Information</h3>
                  <p><strong>Name:</strong> {zoneDetails.name}</p>
                  <p><strong>Code:</strong> {zoneDetails.code}</p>
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="zoneId">Zone ID</label>
                  <input
                    id="zoneId"
                    type="text"
                    value={zoneId}
                    onChange={handleZoneIdInputChange}
                    placeholder="Enter parking zone ID"
                    required
                  />
                  <button 
                    className='search-button' 
                    type="button" 
                    onClick={handleZoneIdSearch}
                    disabled={loading || initialLoading || !zoneId.trim()}
                  >
                    Search
                  </button>
                </div>  

                {zoneDetails && !error && ( 
                  <>
                  <div className="form-group">
                  <label htmlFor="licensePlate">License Plate</label>
                  <input
                    id="licensePlate"
                    type="text"
                    value={licensePlate}
                    onChange={handleLicensePlateChange}
                    placeholder="Enter your license plate"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={handleEmailChange}
                    placeholder="Enter your email for receipt"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="duration">Parking Duration</label>
                  <select 
                    id="duration" 
                    value={duration} 
                    onChange={handleDurationChange}
                    required
                    disabled={!zoneDetails || loading || initialLoading || !!error}
                  >
                    <option value="60">1 hour</option>
                    <option value="120">2 hours</option>
                    <option value="180">3 hours</option>
                    <option value="240">4 hours</option>
                    <option value="480">8 hours</option>
                  </select>
                </div>

                {pricingDetails.calculated_price && (
                  <div className="parking-summary">
                    <div className="summary-item">
                      <span>Rate:</span>
                      <span>${pricingDetails.hourly_rate.toFixed(2)}/hour</span>
                    </div>
                    {pricingDetails.base_fee > 0 && (
                      <div className="summary-item">
                        <span>Base Fee:</span>
                        <span>${pricingDetails.base_fee.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="summary-item">
                      <span>Duration:</span>
                      <span>{duration / 60} hour{duration / 60 !== 1 ? 's' : ''}</span>
                    </div>
                    <div className="summary-item">
                      <span>Parking Fee:</span>
                      <span>${pricingDetails.calculated_price.parking_fee.toFixed(2)}</span>
                    </div>
                    <div className="summary-item">
                      <span>Processing Fee:</span>
                      <span>${pricingDetails.calculated_price.processing_fee.toFixed(2)}</span>
                    </div>
                    <div className="summary-item total">
                      <span>Total:</span>
                      <span>${pricingDetails.calculated_price.total_price.toFixed(2)}</span>
                    </div>
                    <div className="summary-item">
                      <span>Expires at:</span>
                      <span>{getExpirationTime()}</span>
                    </div>
                  </div>
                )}
                  </>
                )}

                <button 
                  type="submit" 
                  className="submit-button"
                  disabled={loading || initialLoading || !zoneDetails || !pricingDetails.calculated_price || !!error}
                >
                  {loading ? 'Processing...' : 'Proceed to Payment'}
                </button>
              </form>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default ZonePage; 